import { WALLET_CUSTODY_TYPE, WALLET_PROVIDER } from '../../constants/wallet.constants';
import { getEnvTokenContract } from '../../constants/evmEnv.config';
import {
  getEvmChain,
  isKnownNativeCoinKey,
  resolveChainIdFromCoin,
} from '../../constants/evmChain.registry';

const CUSTODIAL_TYPE_HINTS = new Set([
  'custodial',
  'bitgo',
  'custo',
]);

const NON_CUSTODIAL_TYPE_HINTS = new Set([
  'non_custodial',
  'non-custodial',
  'noncustodial',
  'metamask',
  'trust',
  'walletconnect',
  'coinbase',
]);

const normalizeTypeKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const trimString = (value) => {
  if (value == null) return '';
  return String(value).trim();
};

/** BitGo chain-prefixed coin id: `tbaseeth:tnmr` → `tnmr`. */
export const stripCoinNameChainPrefix = (coinName) => {
  const key = trimString(coinName).toLowerCase();
  if (!key) return '';
  const colonIdx = key.indexOf(':');
  if (colonIdx >= 0) return key.slice(colonIdx + 1);
  return key;
};

/**
 * Map linked-wallet `coinName` to a pair symbol (`nmr`, `usdc`, `tpolygon`, …).
 * Handles chain prefixes (`tbaseeth:tnmr`) and test asset prefixes (`tnmr` → `nmr`).
 */
export const normalizeLinkedCoinSymbol = (coinName) => {
  const stripped = stripCoinNameChainPrefix(coinName);
  if (!stripped) return '';

  if (isKnownNativeCoinKey(stripped)) return stripped;

  if (stripped.startsWith('t') && stripped.length > 1) {
    const withoutTestPrefix = stripped.slice(1);
    if (withoutTestPrefix && !isKnownNativeCoinKey(withoutTestPrefix)) {
      return withoutTestPrefix;
    }
  }

  return stripped;
};

/** True when wallet row `coinName` corresponds to pair asset symbol (e.g. tnmr ↔ nmr). */
export const coinNameMatchesAsset = (coinName, assetSymbol) => {
  const target = trimString(assetSymbol).toLowerCase();
  if (!target) return false;

  const raw = trimString(coinName).toLowerCase();
  if (!raw) return false;

  if (raw === target) return true;
  if (normalizeLinkedCoinSymbol(raw) === target) return true;
  if (raw.endsWith(`:${target}`) || raw.endsWith(`:t${target}`)) return true;

  return false;
};

export const formatShortWalletAddress = (address) => {
  if (!address || typeof address !== 'string') return '-';
  const trimmed = address.trim();
  if (trimmed.length <= 12) return trimmed;
  return `${trimmed.slice(0, 6)}...${trimmed.slice(-4)}`;
};

export const getLinkedWalletAddress = (wallet) => {
  const address = trimString(wallet?.address ?? wallet?.walletAddress);
  if (address) return address;

  const walletId = trimString(wallet?.walletId ?? wallet?.wallet_id);
  if (/^0x[a-fA-F0-9]{40}$/.test(walletId)) return walletId;

  return '';
};

export const getWalletSettlement = (wallet) => {
  const settlement = wallet?.settlement ?? wallet?.raw?.settlement ?? null;
  return settlement && typeof settlement === 'object' ? settlement : null;
};

/** DvP spender — must match EIP-712 domain verifyingContract used at sign time. */
export const resolveEip712VerifyingContract = (wallet) => {
  const settlement = getWalletSettlement(wallet);
  const eip712 = wallet?.eip712 ?? settlement?.eip712 ?? null;

  return (
    trimString(eip712?.verifyingContract ?? eip712?.verifying_contract) ||
    trimString(settlement?.settlementContract ?? settlement?.settlement_contract) ||
    trimString(settlement?.spenderAddress ?? settlement?.spender_address) ||
    trimString(wallet?.spenderAddress ?? wallet?.spender_address) ||
    null
  );
};

/** True for quote/payment wallet rows (usdc, tpolygon:usdc, tbaseeth:usdc, etc.). */
export const isPaymentCoinName = (coinName) => {
  const normalized = normalizeLinkedCoinSymbol(coinName);
  if (!normalized) return false;
  return normalized.includes('usdc') || normalized.includes('usdt');
};

/** True when an address is the settlement/spender contract, not an ERC-20 token. */
const isWalletSpenderAddress = (address, wallet) => {
  const candidate = trimString(address).toLowerCase();
  if (!candidate) return false;

  const settlement = getWalletSettlement(wallet);
  const blocked = [
    wallet?.spenderAddress,
    settlement?.spenderAddress,
    settlement?.spender_address,
    settlement?.settlementContract,
    settlement?.settlement_contract,
    resolveEip712VerifyingContract(wallet),
  ]
    .filter(Boolean)
    .map((value) => trimString(value).toLowerCase());

  return blocked.includes(candidate);
};

/** Asset token from linked-wallets API — asset rows use settlement.tokenAddress as the ERC-20 contract. */
export const resolveSettlementAssetToken = (wallet) => {
  const settlement = getWalletSettlement(wallet);
  const coinName = trimString(
    wallet?.coinName ?? wallet?.raw?.coinName ?? wallet?.raw?.coin_name,
  ).toLowerCase();

  const rowTokenAddress = trimString(
    settlement?.tokenAddress ?? settlement?.token_address,
  );

  const explicit = trimString(
    wallet?.assetTokenAddress ??
      settlement?.assetToken ??
      settlement?.asset_token ??
      settlement?.assetTokenAddress ??
      settlement?.asset_token_address ??
      settlement?.assetContract ??
      settlement?.asset_contract ??
      wallet?.raw?.settlement?.assetToken ??
      wallet?.raw?.settlement?.asset_token,
  );

  if (explicit) {
    const isPaymentRow = isPaymentCoinName(coinName);
    const matchesPayment =
      rowTokenAddress && explicit.toLowerCase() === rowTokenAddress.toLowerCase();
    if ((!isPaymentRow || !matchesPayment) && !isWalletSpenderAddress(explicit, wallet)) {
      return explicit;
    }
  }

  if (isNativePairAsset(coinName)) {
    return rowTokenAddress || null;
  }

  if (!isPaymentCoinName(coinName) && rowTokenAddress) {
    if (isWalletSpenderAddress(rowTokenAddress, wallet)) return null;
    return rowTokenAddress;
  }

  return null;
};

/**
 * Resolve ERC-20 contract candidates for one pair leg (usdc, nmr, …).
 * Includes linked-wallet rows plus chain env/registry fallbacks.
 */
export const resolvePairLegTokenCandidates = (
  wallet,
  linkedWallets,
  selectedPair,
  assetSymbol,
) => {
  const assetKey = trimString(assetSymbol).toLowerCase();
  const baseKey = trimString(selectedPair?.baseCurrency).toLowerCase();
  const otherKey = trimString(selectedPair?.otherCurrency).toLowerCase();
  if (!assetKey) return [];

  const seen = new Set();
  const ordered = [];

  const addCandidate = (addr, contextWallet) => {
    const normalized = trimString(addr);
    if (!normalized || !/^0x[a-fA-F0-9]{40}$/i.test(normalized)) return;
    const dedupeKey = normalized.toLowerCase();
    if (seen.has(dedupeKey)) return;
    if (isWalletSpenderAddress(normalized, contextWallet ?? wallet)) return;
    seen.add(dedupeKey);
    ordered.push(normalized);
  };

  const collectFromWallet = (candidate) => {
    if (!candidate) return;

    const contracts = candidate.tokenContracts ?? {};
    const candidates = [];

    if (assetKey === baseKey) {
      candidates.push(
        candidate.paymentTokenAddress,
        resolveSettlementPaymentToken(candidate),
        contracts[assetKey],
      );
    } else if (assetKey === otherKey) {
      candidates.push(
        candidate.assetTokenAddress,
        resolveSettlementAssetToken(candidate),
        contracts[assetKey],
      );
    } else {
      candidates.push(contracts[assetKey]);
    }

    Object.entries(contracts).forEach(([symbol, address]) => {
      if (coinNameMatchesAsset(symbol, assetKey)) {
        candidates.push(address);
      }
    });

    candidates.forEach((addr) => addCandidate(addr, candidate));
  };

  const chainId = resolveWalletChainId(wallet);
  const rowsToTry = [
    wallet,
    ...(linkedWallets ?? []).filter((row) => {
      if (!coinNameMatchesAsset(row?.coinName, assetKey)) return false;
      if (!coinNameMatchesSettlementChain(row?.coinName, chainId)) return false;
      const rowChainId = resolveWalletChainId(row);
      if (chainId != null && rowChainId != null && rowChainId !== chainId) return false;
      return true;
    }),
  ];

  rowsToTry.forEach(collectFromWallet);

  const envToken = getEnvTokenContract(chainId, assetKey);
  if (envToken) addCandidate(envToken, wallet);

  return ordered;
};

export const resolvePairLegTokenAddress = (
  wallet,
  linkedWallets,
  selectedPair,
  assetSymbol,
) => resolvePairLegTokenCandidates(wallet, linkedWallets, selectedPair, assetSymbol)[0] ?? null;

const appendUniqueRpcUrl = (urls, value) => {
  const trimmed = trimString(value);
  if (!trimmed || urls.includes(trimmed)) return;
  urls.push(trimmed);
};

/** Read RPC URLs from linked-wallets API — settlement.rpcUrl first, then static registry fallback. */
export const resolveWalletReadRpcUrls = (wallet, pairContext = null) => {
  const settlement = getWalletSettlement(wallet);
  const urls = [];

  appendUniqueRpcUrl(urls, wallet?.rpcUrl);
  appendUniqueRpcUrl(urls, settlement?.rpcUrl ?? settlement?.rpc_url);
  appendUniqueRpcUrl(urls, settlement?.readRpcUrl ?? settlement?.read_rpc_url);
  appendUniqueRpcUrl(urls, pairContext?.rpcUrl ?? pairContext?.rpc_url);

  [
    settlement?.rpcUrls,
    settlement?.rpc_urls,
    settlement?.readRpcUrls,
    settlement?.read_rpc_urls,
    pairContext?.rpcUrls,
    pairContext?.rpc_urls,
  ].forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((url) => appendUniqueRpcUrl(urls, url));
  });

  return urls;
};

/** MetaMask add/update network params — API settlement fields first, static registry fallback. */
export const resolveWalletMetaMaskChainParams = (wallet, pairContext = null) => {
  const chainId = resolveWalletChainId(wallet);
  if (!Number.isFinite(chainId)) return null;

  const settlement = getWalletSettlement(wallet);
  const apiRpcUrls = resolveWalletReadRpcUrls(wallet, pairContext);
  const staticChain = getEvmChain(chainId);

  const rpcUrls = [
    ...apiRpcUrls,
    ...(staticChain?.readRpcUrls ?? []),
    ...(staticChain?.rpcUrls ?? []),
  ].filter((url, index, arr) => url && arr.indexOf(url) === index);

  if (!rpcUrls.length) return null;

  const chainName =
    trimString(settlement?.chainName ?? settlement?.chain_name ?? wallet?.chainName ?? wallet?.chain_name) ||
    staticChain?.chainName ||
    `Chain ${chainId}`;

  const nativeName =
    trimString(settlement?.nativeCurrencyName ?? settlement?.native_currency_name) ||
    staticChain?.nativeCurrency?.name ||
    'Ether';

  const nativeSymbol =
    trimString(settlement?.nativeCurrencySymbol ?? settlement?.native_currency_symbol) ||
    staticChain?.nativeCurrency?.symbol ||
    'ETH';

  const nativeDecimalsRaw = Number(
    settlement?.nativeCurrencyDecimals ?? settlement?.native_currency_decimals,
  );
  const nativeDecimals = Number.isFinite(nativeDecimalsRaw)
    ? nativeDecimalsRaw
    : staticChain?.nativeCurrency?.decimals ?? 18;

  const blockExplorerUrls = [];
  const explorer = trimString(
    settlement?.blockExplorerUrl ??
      settlement?.block_explorer_url ??
      settlement?.explorerUrl ??
      settlement?.explorer_url,
  );
  if (explorer) blockExplorerUrls.push(explorer);
  (staticChain?.blockExplorerUrls ?? []).forEach((url) => appendUniqueRpcUrl(blockExplorerUrls, url));

  return {
    chainId: `0x${Number(chainId).toString(16)}`,
    chainName,
    nativeCurrency: {
      name: nativeName,
      symbol: nativeSymbol,
      decimals: nativeDecimals,
    },
    rpcUrls,
    blockExplorerUrls,
  };
};

export const resolveWalletNativeCurrencySymbol = (wallet) => {
  const settlement = getWalletSettlement(wallet);
  const fromApi = trimString(
    settlement?.nativeCurrencySymbol ?? settlement?.native_currency_symbol,
  );
  if (fromApi) return fromApi;

  const chainId = resolveWalletChainId(wallet);
  return getEvmChain(chainId)?.nativeCurrency?.symbol ?? null;
};

/** Label for native gas token row in Accounts (e.g. eth on Base Sepolia). */
export const resolveNativeAccountAssetLabel = (wallet, linkedWallets = []) => {
  const symbol = resolveWalletNativeCurrencySymbol(wallet);
  if (symbol) return symbol.toLowerCase();

  const chainId = resolveWalletChainId(wallet);

  for (const row of linkedWallets) {
    const coinName = trimString(row?.coinName ?? row?.raw?.coinName).toLowerCase();
    if (!coinName || coinName.includes(':')) continue;
    if (isNativePairAsset(coinName) && coinNameMatchesSettlementChain(coinName, chainId)) {
      return coinName;
    }
  }

  const walletCoin = trimString(wallet?.coinName).toLowerCase();
  const prefix = getCoinNameChainPrefix(walletCoin);
  if (prefix && isKnownNativeCoinKey(prefix)) {
    return prefix;
  }

  return 'eth';
};

/** Payment token contract from linked-wallets API settlement.tokenAddress on payment rows only. */
export const resolveSettlementPaymentToken = (wallet) => {
  const settlement = getWalletSettlement(wallet);
  const coinName = trimString(
    wallet?.coinName ?? wallet?.raw?.coinName ?? wallet?.raw?.coin_name,
  ).toLowerCase();

  if (!isPaymentCoinName(coinName)) return null;

  const token = trimString(settlement?.tokenAddress ?? settlement?.token_address) || null;
  if (!token || isWalletSpenderAddress(token, wallet)) return null;
  return token;
};

export const resolveWalletCustodyType = (wallet) => {
  const rawType = normalizeTypeKey(
    wallet?.custodyType ??
      wallet?.custody_type ??
      wallet?.walletType ??
      wallet?.wallet_type ??
      wallet?.type ??
      wallet?.provider,
  );

  if (NON_CUSTODIAL_TYPE_HINTS.has(rawType)) {
    return WALLET_CUSTODY_TYPE.NON_CUSTODIAL;
  }
  if (CUSTODIAL_TYPE_HINTS.has(rawType)) {
    return WALLET_CUSTODY_TYPE.CUSTODIAL;
  }
  if (wallet?.isCustodial === false || wallet?.is_custodial === false) {
    return WALLET_CUSTODY_TYPE.NON_CUSTODIAL;
  }
  if (wallet?.isCustodial === true || wallet?.is_custodial === true) {
    return WALLET_CUSTODY_TYPE.CUSTODIAL;
  }
  return WALLET_CUSTODY_TYPE.CUSTODIAL;
};

export const isCustodialWallet = (wallet) =>
  resolveWalletCustodyType(wallet) === WALLET_CUSTODY_TYPE.CUSTODIAL;

export const isNonCustodialWallet = (wallet) =>
  resolveWalletCustodyType(wallet) === WALLET_CUSTODY_TYPE.NON_CUSTODIAL;

const resolveWalletTypeLabel = (wallet, custodyType) => {
  const explicit =
    wallet?.walletTypeLabel ??
    wallet?.wallet_type_label ??
    wallet?.providerLabel ??
    wallet?.provider_label;
  if (explicit) return String(explicit);

  const rawType = normalizeTypeKey(
    wallet?.walletType ?? wallet?.wallet_type ?? wallet?.type ?? wallet?.provider,
  );

  if (rawType.includes('bitgo') || custodyType === WALLET_CUSTODY_TYPE.CUSTODIAL) {
    return 'BitGo';
  }
  if (rawType.includes('metamask')) return 'MetaMask';
  if (rawType.includes('trust')) return 'Trust Wallet';
  if (rawType.includes('walletconnect')) return 'WalletConnect';
  if (rawType.includes('coinbase')) return 'Coinbase Wallet';
  if (custodyType === WALLET_CUSTODY_TYPE.NON_CUSTODIAL) return 'Non-Custodial';
  return 'Custodial';
};

const resolveWalletProvider = (wallet, custodyType) => {
  const raw = normalizeTypeKey(wallet?.provider ?? wallet?.walletProvider ?? wallet?.wallet_provider);
  if (raw.includes('metamask')) return WALLET_PROVIDER.METAMASK;
  if (raw.includes('bitgo') || custodyType === WALLET_CUSTODY_TYPE.CUSTODIAL) {
    return WALLET_PROVIDER.BITGO;
  }
  if (custodyType === WALLET_CUSTODY_TYPE.NON_CUSTODIAL) {
    return raw || WALLET_PROVIDER.METAMASK;
  }
  return WALLET_PROVIDER.BITGO;
};

/** Raw walletType from linked-wallets API (e.g. BITGO, METAMASK). */
export const resolveLinkedWalletType = (wallet) => {
  const value = trimString(
    wallet?.walletType ??
      wallet?.wallet_type ??
      wallet?.type ??
      wallet?.provider,
  );
  return value || null;
};

const addTokenContract = (contracts, symbol, address) => {
  const key = trimString(symbol).toLowerCase();
  const normalizedAddress = trimString(address);
  if (!key || !normalizedAddress) return;
  if (isNativePairAsset(key)) return;
  contracts[key] = normalizedAddress;
};

const registerCoinTokenContract = (contracts, coinName, address) => {
  const raw = trimString(coinName).toLowerCase();
  if (!raw) return;
  addTokenContract(contracts, raw, address);
  const normalized = normalizeLinkedCoinSymbol(raw);
  if (!normalized || normalized === raw) return;

  const chainPrefix = getCoinNameChainPrefix(raw);
  if (!chainPrefix) {
    addTokenContract(contracts, normalized, address);
    return;
  }

  // Chain-prefixed payment rows (tpolygon:usdc) must not alias to bare usdc — wrong chain.
  if (!isPaymentCoinName(raw)) {
    addTokenContract(contracts, normalized, address);
  }
};

export const isNativePairAsset = (assetSymbol) => {
  const key = trimString(assetSymbol).toLowerCase();
  if (!key) return false;
  if (key.includes(':')) return false;
  if (key.includes('usdc') || key.includes('usdt')) return false;
  return isKnownNativeCoinKey(key);
};

const isErc20PairAsset = (assetSymbol) => {
  const key = trimString(assetSymbol).toLowerCase();
  if (!key) return false;
  if (key.includes(':')) return true;
  if (key.includes('usdc') || key.includes('usdt')) return true;
  return !isNativePairAsset(key);
};

const stripNativeTokenContracts = (contracts) => {
  if (!contracts || typeof contracts !== 'object') return {};
  return Object.entries(contracts).reduce((acc, [symbol, address]) => {
    if (isNativePairAsset(symbol)) return acc;
    acc[trimString(symbol).toLowerCase()] = trimString(address);
    return acc;
  }, {});
};

const extractTokenContracts = (wallet, settlement, pairContext = {}) => {
  const contracts = {};
  const sources = [
    wallet?.tokenContracts,
    wallet?.token_contracts,
    wallet?.assets,
    wallet?.assetList,
    wallet?.asset_list,
  ];

  sources.forEach((source) => {
    if (!source) return;
    if (Array.isArray(source)) {
      source.forEach((asset) => {
        const symbol = trimString(
          asset?.symbol ?? asset?.asset_name ?? asset?.coin ?? asset?.name,
        ).toLowerCase();
        const address =
          asset?.contractAddress ??
          asset?.contract_address ??
          asset?.tokenAddress ??
          asset?.token_address ??
          asset?.address;
        addTokenContract(contracts, symbol, address);
      });
      return;
    }
    if (typeof source === 'object') {
      Object.entries(source).forEach(([symbol, address]) => {
        addTokenContract(contracts, symbol, address);
      });
    }
  });

  const rowToken = trimString(
    settlement?.tokenAddress ?? settlement?.token_address,
  );
  const pairPaymentToken = trimString(
    pairContext.paymentToken ?? pairContext.payment_token,
  );
  const walletCoin = trimString(wallet.coinName ?? wallet.coin_name).toLowerCase();
  const quoteCoin = trimString(pairContext.quoteCoin ?? pairContext.quote_coin).toLowerCase();

  // Each linked-wallet row: settlement.tokenAddress is THAT coin's ERC-20 contract.
  if (rowToken && walletCoin) {
    const rowChainId = resolveWalletChainId({ settlement, coinName: walletCoin });
    if (coinNameMatchesSettlementChain(walletCoin, rowChainId)) {
      registerCoinTokenContract(contracts, walletCoin, rowToken);
      const normalizedCoin = normalizeLinkedCoinSymbol(walletCoin);
      if (normalizedCoin.includes('usdc')) addTokenContract(contracts, 'tusdc', rowToken);
      if (normalizedCoin.includes('usdt')) addTokenContract(contracts, 'tusdt', rowToken);
    }
  }

  // Optional pair-level payment token override (quote coin only).
  if (pairPaymentToken && quoteCoin) {
    addTokenContract(contracts, quoteCoin, pairPaymentToken);
    if (quoteCoin.includes('usdc')) addTokenContract(contracts, 'tusdc', pairPaymentToken);
    if (quoteCoin.includes('usdt')) addTokenContract(contracts, 'tusdt', pairPaymentToken);
  }

  const pairAssetToken = trimString(pairContext.assetToken ?? pairContext.asset_token);
  if (pairAssetToken) {
    const assetCoin = trimString(
      pairContext.baseCoin ??
        pairContext.base_coin ??
        pairContext.otherCoin ??
        pairContext.other_coin ??
        pairContext.assetCoin ??
        pairContext.asset_coin,
    ).toLowerCase();
    if (assetCoin) {
      addTokenContract(contracts, assetCoin, pairAssetToken);
    } else {
      const symbol = trimString(pairContext.symbol).toLowerCase();
      const assetFromSymbol = symbol.includes('/')
        ? symbol.split('/')[0]
        : symbol.includes('_')
          ? symbol.split('_')[0]
          : '';
      if (assetFromSymbol) {
        addTokenContract(contracts, assetFromSymbol, pairAssetToken);
      }
    }
  }

  return stripNativeTokenContracts(contracts);
};

export const resolveWalletChainId = (wallet) => {
  const settlement = getWalletSettlement(wallet);
  const fromSettlement = settlement?.chainId ?? settlement?.chain_id;
  if (fromSettlement != null && fromSettlement !== '') {
    const parsed = Number(fromSettlement);
    if (Number.isFinite(parsed)) return parsed;
  }

  const explicit =
    wallet?.chainId ??
    wallet?.chain_id ??
    wallet?.networkId ??
    wallet?.network_id ??
    null;
  if (explicit != null && explicit !== '') {
    const parsed = Number(explicit);
    if (Number.isFinite(parsed)) return parsed;
  }

  const coinKey = trimString(wallet?.coinName ?? wallet?.coin_name).toLowerCase();
  const coinChainId = coinKey ? resolveChainIdFromCoin(coinKey) : null;
  if (coinChainId != null) return coinChainId;

  return null;
};

export const normalizeLinkedWallet = (wallet, pairContext = {}) => {
  if (!wallet || typeof wallet !== 'object') return null;

  const custodyType = resolveWalletCustodyType(wallet);
  const walletId = trimString(
    wallet.walletId ?? wallet.wallet_id ?? wallet.id ?? wallet.address ?? '',
  ) || null;
  const address = trimString(wallet.address ?? wallet.walletAddress ?? wallet.wallet_address);
  const settlement = getWalletSettlement(wallet);
  const chainId = resolveWalletChainId(wallet);
  const coinName = trimString(wallet.coinName ?? wallet.coin_name).toLowerCase() || null;
  const tokenContracts = extractTokenContracts(wallet, settlement, pairContext);
  const pairAssetToken = trimString(pairContext.assetToken ?? pairContext.asset_token);
  const pairPaymentToken = trimString(pairContext.paymentToken ?? pairContext.payment_token);
  const assetTokenAddress =
    resolveSettlementAssetToken({ settlement, raw: wallet, coinName }) ||
    pairAssetToken ||
    null;
  const paymentTokenAddress =
    resolveSettlementPaymentToken({ settlement, raw: wallet, coinName }) ||
    pairPaymentToken ||
    null;

  const spenderAddress =
    resolveEip712VerifyingContract({ settlement, eip712: settlement?.eip712 ?? null }) ??
    (trimString(
      settlement?.spenderAddress ??
        settlement?.spender_address ??
        settlement?.settlementContract ??
        settlement?.settlement_contract ??
        wallet.spenderAddress ??
        wallet.spender_address ??
        wallet.approvalSpender ??
        wallet.approval_spender ??
        wallet.settlementContract ??
        wallet.settlement_contract ??
        '',
    ) || null);

  return {
    walletId,
    coinName,
    walletName: wallet.walletName ?? wallet.wallet_name ?? wallet.name ?? '-',
    walletLabel: wallet.walletLabel ?? wallet.wallet_label ?? wallet.label ?? '-',
    custodyType,
    walletType: resolveLinkedWalletType(wallet),
    walletTypeLabel: resolveWalletTypeLabel(wallet, custodyType),
    provider: resolveWalletProvider(wallet, custodyType),
    address,
    shortAddress: formatShortWalletAddress(address),
    chainId: Number.isFinite(chainId) ? chainId : null,
    spenderAddress: spenderAddress ? trimString(spenderAddress) : null,
    assetTokenAddress,
    paymentTokenAddress,
    settlement,
    rpcUrl:
      trimString(settlement?.rpcUrl ?? settlement?.rpc_url) ||
      trimString(pairContext?.rpcUrl ?? pairContext?.rpc_url) ||
      null,
    eip712: settlement?.eip712 ?? null,
    tokenContracts,
    raw: wallet,
  };
};

const isWalletLikeObject = (item) =>
  item &&
  typeof item === 'object' &&
  !Array.isArray(item) &&
  (item.walletId != null ||
    item.wallet_id != null ||
    item.address != null ||
    item.walletName != null ||
    item.wallet_name != null);

const extractLinkedWalletRows = (data) => {
  if (!data) return [];

  if (Array.isArray(data)) return data;

  if (typeof data !== 'object') return [];

  const custodialRows = data.custodial ?? data.custodial_wallets ?? data.custodialWallets;
  const nonCustodialRows =
    data.nonCustodial ??
    data.non_custodial ??
    data.nonCustodialWallets ??
    data.non_custodial_wallets;

  if (Array.isArray(custodialRows) || Array.isArray(nonCustodialRows)) {
    return [
      ...(Array.isArray(custodialRows) ? custodialRows : []),
      ...(Array.isArray(nonCustodialRows) ? nonCustodialRows : []),
    ];
  }

  const flatCandidates = [
    data.wallets,
    data.linkedWallets,
    data.linked_wallets,
    data.wallet_list,
    data.walletList,
    data.list,
    data.rows,
  ];

  for (const candidate of flatCandidates) {
    if (Array.isArray(candidate) && candidate.length) {
      return candidate;
    }
  }

  const flattened = [];
  Object.values(data).forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (isWalletLikeObject(item)) flattened.push(item);
      });
      return;
    }
    if (isWalletLikeObject(value)) flattened.push(value);
  });

  return flattened;
};

export const normalizeLinkedWalletsResponse = (body) => {
  if (!body || typeof body !== 'object') {
    return { wallets: [], totalCount: 0 };
  }

  const data = body.data !== undefined ? body.data : body;
  const rows = extractLinkedWalletRows(data);
  const pairContext = {
    baseCoin: data?.baseCoin ?? data?.base_coin,
    quoteCoin: data?.quoteCoin ?? data?.quote_coin,
    symbol: data?.symbol,
    assetToken: data?.assetToken ?? data?.asset_token,
    paymentToken: data?.paymentToken ?? data?.payment_token,
    rpcUrl: data?.rpcUrl ?? data?.rpc_url,
    rpcUrls: data?.rpcUrls ?? data?.rpc_urls,
    chainName: data?.chainName ?? data?.chain_name,
    nativeCurrencySymbol: data?.nativeCurrencySymbol ?? data?.native_currency_symbol,
    blockExplorerUrl: data?.blockExplorerUrl ?? data?.block_explorer_url,
  };

  const wallets = rows
    .map((row) => normalizeLinkedWallet(row, pairContext))
    .filter(Boolean)
    .filter((wallet) => coinNameMatchesSettlementChain(wallet.coinName, wallet.chainId));

  const totalCountRaw = Number(
    data?.total_count ??
      data?.totalCount ??
      data?.count ??
      body?.total_count ??
      body?.totalCount,
  );

  return {
    wallets,
    totalCount:
      Number.isFinite(totalCountRaw) && totalCountRaw >= 0
        ? Math.floor(totalCountRaw)
        : wallets.length,
  };
};

export const buildTradeBalanceParams = ({
  baseCurrency,
  otherCurrency,
  walletId,
  selectedTradeWallet,
}) => {
  const params = {
    baseCurrency,
    otherCurrency,
  };
  const resolvedWalletId =
    walletId ??
    selectedTradeWallet?.walletId ??
    selectedTradeWallet?.wallet_id;
  if (resolvedWalletId != null && String(resolvedWalletId).trim() !== '') {
    params.walletId = resolvedWalletId;
  }
  return params;
};

/**
 * Linked-wallets API symbol: {otherCurrency}_{baseCurrency}
 * e.g. tpolygon + tpolygon:usdc → tpolygon_tpolygon:usdc
 */
export const buildLinkedWalletsSymbol = (selectedPair) => {
  const baseCurrency = trimString(selectedPair?.baseCurrency).toLowerCase();
  const otherCurrency = trimString(selectedPair?.otherCurrency).toLowerCase();

  if (otherCurrency && baseCurrency) {
    return `${otherCurrency}_${baseCurrency}`;
  }

  const pairKey = trimString(selectedPair?.pair_key).toLowerCase();
  if (pairKey) {
    return pairKey.replace(/\//g, '_');
  }

  return '';
};

export const buildTradeOrderBody = (orderBody, selectedTradeWallet) => {
  if (!selectedTradeWallet?.walletId) return orderBody;

  const walletType =
    selectedTradeWallet.walletType ??
    resolveLinkedWalletType(selectedTradeWallet.raw ?? selectedTradeWallet);

  return {
    ...orderBody,
    walletId: selectedTradeWallet.walletId,
    ...(walletType ? { walletType } : {}),
  };
};

/** Asset spent when placing an order: buy → base, sell → other (quote quantity asset). */
export const getPaymentAssetForSide = (selectedPair, selectedSide) => {
  if (!selectedPair) return '';
  return selectedSide === 0
    ? trimString(selectedPair.baseCurrency).toLowerCase()
    : trimString(selectedPair.otherCurrency).toLowerCase();
};

export const getPaymentAssetLabel = (selectedPair, selectedSide) =>
  getPaymentAssetForSide(selectedPair, selectedSide).toUpperCase();

export const walletSupportsPaymentAsset = (wallet, paymentAsset) => {
  if (!wallet || !paymentAsset) return false;

  const coinName = trimString(
    wallet.coinName ?? wallet.raw?.coinName ?? wallet.raw?.coin_name,
  ).toLowerCase();
  if (!coinName) return true;

  return coinNameMatchesAsset(coinName, paymentAsset);
};

const getCoinNameChainPrefix = (coinName) => {
  const raw = trimString(coinName).toLowerCase();
  if (!raw.includes(':')) return null;
  return raw.split(':')[0] || null;
};

/** True when BitGo coin prefix (tpolygon, tbaseeth) matches settlement.chainId. */
export const coinNameMatchesSettlementChain = (coinName, chainId) => {
  const prefix = getCoinNameChainPrefix(coinName);
  if (!prefix) return true;

  const prefixChainId = resolveChainIdFromCoin(prefix);
  if (prefixChainId == null) return true;

  const settlementChainId = Number(chainId);
  if (!Number.isFinite(settlementChainId)) return true;

  return prefixChainId === settlementChainId;
};

/** Chain prefixes from asset-leg rows (e.g. tbaseeth:tnmr → tbaseeth for nmr/usdc). */
const derivePairChainPrefixes = (wallets, selectedPair) => {
  const assetSymbol = trimString(selectedPair?.otherCurrency).toLowerCase();
  if (!assetSymbol) return new Set();

  const prefixes = new Set();
  (wallets ?? []).forEach((wallet) => {
    const coinName = trimString(wallet?.coinName ?? wallet?.raw?.coinName).toLowerCase();
    const prefix = getCoinNameChainPrefix(coinName);
    if (!prefix) return;
    if (coinNameMatchesAsset(coinName, assetSymbol)) {
      prefixes.add(prefix);
    }
  });
  return prefixes;
};

const scoreBalanceRowForPairAsset = (
  row,
  pairAsset,
  selectedTradeWallet,
  pairChainPrefixes,
) => {
  const rawName = trimString(row?.asset_name ?? row?.assetName).toLowerCase();
  if (!rawName || !coinNameMatchesAsset(rawName, pairAsset)) return -1;

  let score = 0;
  const prefix = getCoinNameChainPrefix(rawName);
  if (prefix && pairChainPrefixes.has(prefix)) score += 10;
  if (!prefix) score += 5;

  const walletCoin = trimString(selectedTradeWallet?.coinName).toLowerCase();
  if (walletCoin && coinNameMatchesAsset(walletCoin, pairAsset)) {
    const walletPrefix = getCoinNameChainPrefix(walletCoin);
    if (walletPrefix && prefix === walletPrefix) score += 20;
    if (!walletPrefix && !prefix) score += 15;
  }

  const walletChainId = resolveWalletChainId(selectedTradeWallet);
  if (walletChainId && prefix) {
    const rowChainId = resolveChainIdFromCoin(prefix);
    if (rowChainId === walletChainId) score += 15;
  }

  const available = Number(row?.available_balance ?? row?.availableBalance ?? 0);
  if (Number.isFinite(available)) {
    score += Math.min(Math.max(available, 0), 1_000_000) / 1_000_000;
  }

  return score;
};

/**
 * Reduce custodial/legacy balance payloads to the active pair legs only (`usdc`, `nmr`, …).
 * Picks the best chain-scoped row per asset and normalizes `asset_name` to pair symbols.
 */
export const normalizePairBalanceForTrade = (
  balance,
  selectedPair,
  selectedTradeWallet = null,
  linkedWallets = [],
) => {
  if (!balance || typeof balance !== 'object') return balance;
  if (!selectedPair?.baseCurrency || !selectedPair?.otherCurrency) return balance;

  const base = trimString(selectedPair.baseCurrency).toLowerCase();
  const other = trimString(selectedPair.otherCurrency).toLowerCase();
  const rows = Array.isArray(balance.wallet) ? balance.wallet : [];
  const walletSource = linkedWallets?.length
    ? linkedWallets
    : rows.map((row) => ({
        coinName: row?.asset_name ?? row?.assetName,
      }));
  const pairChainPrefixes = derivePairChainPrefixes(walletSource, selectedPair);

  const pickBestRow = (pairAsset) => {
    let best = null;
    let bestScore = -1;
    rows.forEach((row) => {
      const score = scoreBalanceRowForPairAsset(
        row,
        pairAsset,
        selectedTradeWallet,
        pairChainPrefixes,
      );
      if (score > bestScore) {
        bestScore = score;
        best = row;
      }
    });
    if (!best) return null;
    return {
      ...best,
      asset_name: pairAsset,
    };
  };

  const wallet = [pickBestRow(base), pickBestRow(other)].filter(Boolean);

  return {
    ...balance,
    wallet,
  };
};

/** Pair-scoped wallet rows for order placement / insufficient-balance checks. */
export const getPairWalletFromBalance = (balance) => {
  if (Array.isArray(balance?.pairWallet) && balance.pairWallet.length) {
    return balance.pairWallet;
  }
  return Array.isArray(balance?.wallet) ? balance.wallet : [];
};

const normalizeBalanceRowWalletType = (row) =>
  trimString(row?.wallet_type ?? row?.walletType).toUpperCase();

const normalizeBalanceRowWalletId = (row) =>
  trimString(row?.wallet_id ?? row?.walletId);

/**
 * Accounts panel rows scoped to the selected trade wallet (wallet_type, wallet_id, address).
 */
export const filterBalanceWalletForTradeWallet = (
  walletRows,
  selectedTradeWallet,
) => {
  if (!Array.isArray(walletRows) || !walletRows.length) return [];
  if (!selectedTradeWallet) return [];

  const selectedType = trimString(
    resolveLinkedWalletType(selectedTradeWallet) ??
      selectedTradeWallet?.walletType ??
      selectedTradeWallet?.wallet_type,
  ).toUpperCase();
  const selectedWalletId = trimString(
    selectedTradeWallet?.walletId ?? selectedTradeWallet?.wallet_id,
  );
  const selectedAddress = trimString(
    getLinkedWalletAddress(selectedTradeWallet),
  ).toLowerCase();

  const rowsHaveWalletType = walletRows.some((row) =>
    Boolean(normalizeBalanceRowWalletType(row)),
  );
  const rowsHaveWalletId = walletRows.some((row) =>
    Boolean(normalizeBalanceRowWalletId(row)),
  );

  return walletRows.filter((row) => {
    const rowType = normalizeBalanceRowWalletType(row);
    const rowWalletId = normalizeBalanceRowWalletId(row);
    const rowAddress = trimString(row?.address).toLowerCase();

    if (rowsHaveWalletId && selectedWalletId && rowWalletId && rowWalletId !== selectedWalletId) {
      return false;
    }
    if (rowsHaveWalletType && selectedType && rowType && rowType !== selectedType) {
      return false;
    }
    if (selectedAddress && rowAddress && rowAddress !== selectedAddress) {
      return false;
    }
    return true;
  });
};

/**
 * Preserve full API wallet rows for Accounts while attaching pair-scoped legs for trading.
 */
export const enrichBalanceWithPairWallet = (
  balance,
  selectedPair,
  selectedTradeWallet = null,
  linkedWallets = [],
) => {
  if (!balance || typeof balance !== 'object') return balance;

  const fullWallet = Array.isArray(balance.wallet) ? balance.wallet : [];
  if (!selectedPair?.baseCurrency || !selectedPair?.otherCurrency) {
    return {
      ...balance,
      wallet: fullWallet,
      pairWallet: fullWallet,
    };
  }

  const normalized = normalizePairBalanceForTrade(
    { ...balance, wallet: fullWallet },
    selectedPair,
    selectedTradeWallet,
    linkedWallets,
  );

  return {
    ...balance,
    wallet: fullWallet,
    pairWallet: normalized?.wallet ?? [],
  };
};

const walletRowMatchesPairCoins = (wallet, selectedPair) => {
  const base = trimString(selectedPair?.baseCurrency).toLowerCase();
  const other = trimString(selectedPair?.otherCurrency).toLowerCase();
  const normalized = normalizeLinkedCoinSymbol(wallet?.coinName);
  if (!normalized) return false;
  return normalized === base || normalized === other;
};

const walletRowMatchesPairChain = (wallet, pairChainPrefixes) => {
  if (!pairChainPrefixes.size) return true;
  const prefix = getCoinNameChainPrefix(wallet?.coinName);
  if (!prefix) return true;
  return pairChainPrefixes.has(prefix);
};

const getWalletIdentityKey = (wallet) => {
  const address = trimString(getLinkedWalletAddress(wallet)).toLowerCase();
  if (address) return `addr:${address}`;

  const walletId = trimString(wallet?.walletId ?? wallet?.raw?.walletId);
  const accountId = trimString(
    wallet?.accountId ??
      wallet?.account_id ??
      wallet?.raw?.accountId ??
      wallet?.raw?.account_id,
  );
  const custody = trimString(wallet?.custodyType);
  return `id:${walletId}:${accountId}:${custody}`;
};

const scoreWalletRowForPaymentSide = (wallet, pairChainPrefixes) => {
  let score = 0;
  const prefix = getCoinNameChainPrefix(wallet?.coinName);
  if (prefix && pairChainPrefixes.has(prefix)) score += 10;
  if (!prefix) score += pairChainPrefixes.size > 0 ? 3 : 5;
  return score;
};

/** True when wallet row can pay for the given pair side (buy → quote, sell → asset). */
export const walletMatchesSideForPair = (
  wallet,
  selectedPair,
  selectedSide,
  allWallets = [],
) => {
  const paymentAsset = getPaymentAssetForSide(selectedPair, selectedSide);
  if (!paymentAsset) return false;

  const pairChainPrefixes = derivePairChainPrefixes(allWallets, selectedPair);
  return (
    walletSupportsPaymentAsset(wallet, paymentAsset) &&
    walletRowMatchesPairCoins(wallet, selectedPair) &&
    walletRowMatchesPairChain(wallet, pairChainPrefixes)
  );
};

export const filterLinkedWalletsForSide = (wallets, selectedPair, selectedSide) => {
  const allWallets = wallets ?? [];
  const pairChainPrefixes = derivePairChainPrefixes(allWallets, selectedPair);

  const scoped = allWallets.filter((wallet) =>
    walletMatchesSideForPair(wallet, selectedPair, selectedSide, allWallets),
  );

  const byIdentity = new Map();
  scoped.forEach((wallet) => {
    const identityKey = getWalletIdentityKey(wallet);
    const existing = byIdentity.get(identityKey);
    if (!existing) {
      byIdentity.set(identityKey, wallet);
      return;
    }
    const nextScore = scoreWalletRowForPaymentSide(wallet, pairChainPrefixes);
    const existingScore = scoreWalletRowForPaymentSide(existing, pairChainPrefixes);
    if (nextScore > existingScore) {
      byIdentity.set(identityKey, wallet);
    }
  });

  return Array.from(byIdentity.values());
};

export const getLinkedWalletRowKey = (wallet) =>
  `${String(wallet?.walletId ?? wallet?.address ?? 'wallet')}:${wallet?.coinName ?? wallet?.custodyType ?? 'default'}`;

const walletsShareIdentity = (a, b) => {
  if (!a || !b) return false;
  const addressA = trimString(a.address).toLowerCase();
  const addressB = trimString(b.address).toLowerCase();
  if (addressA && addressB && addressA === addressB) return true;
  return trimString(a.walletId) === trimString(b.walletId);
};

const walletsShareChain = (a, b) => {
  const chainA = resolveWalletChainId(a);
  const chainB = resolveWalletChainId(b);
  if (chainA == null || chainB == null) return true;
  return chainA === chainB;
};

const filterSiblingsForWallet = (wallet, allWallets) =>
  (allWallets ?? []).filter(
    (candidate) =>
      walletsShareIdentity(wallet, candidate) && walletsShareChain(wallet, candidate),
  );

const pickSiblingForRole = (siblings, wallet, isPaymentRow) => {
  const matches = siblings.filter((sibling) =>
    isPaymentRow
      ? isPaymentCoinName(sibling?.coinName)
      : !isPaymentCoinName(sibling?.coinName),
  );
  if (!matches.length) return null;
  const sameCoin = matches.find(
    (sibling) =>
      trimString(sibling?.coinName).toLowerCase() ===
      trimString(wallet?.coinName).toLowerCase(),
  );
  return sameCoin ?? matches[0];
};

export const resolveTradeWalletContext = (wallet, linkedWallets) =>
  enrichWalletFromLinkedSiblings(wallet, linkedWallets);

/** Merge tokenContracts from linked rows that share the same on-chain address. */
export const enrichWalletFromLinkedSiblings = (wallet, allWallets) => {
  if (!wallet || !allWallets?.length) return wallet;

  const chainId = resolveWalletChainId(wallet);
  const siblings = filterSiblingsForWallet(wallet, allWallets).filter((sibling) =>
    coinNameMatchesSettlementChain(sibling?.coinName, chainId),
  );
  if (!siblings.length) return wallet;

  const mergedContracts = {};
  siblings.forEach((sibling) => {
    Object.assign(mergedContracts, stripNativeTokenContracts(sibling.tokenContracts ?? {}));
  });

  const paymentSibling = pickSiblingForRole(siblings, wallet, true);
  if (paymentSibling?.tokenContracts) {
    Object.entries(paymentSibling.tokenContracts).forEach(([symbol, address]) => {
      if (isPaymentCoinName(symbol)) {
        mergedContracts[trimString(symbol).toLowerCase()] = trimString(address);
      }
    });
    const paymentSymbol = trimString(paymentSibling.coinName).toLowerCase();
    const paymentToken = resolveSettlementPaymentToken(paymentSibling);
    if (paymentToken && paymentSymbol) {
      mergedContracts[paymentSymbol] = paymentToken;
      const normalizedPayment = normalizeLinkedCoinSymbol(paymentSymbol);
      if (normalizedPayment && !getCoinNameChainPrefix(paymentSymbol)) {
        mergedContracts[normalizedPayment] = paymentToken;
      }
    }
  }

  const assetSibling = pickSiblingForRole(siblings, wallet, false);
  if (assetSibling?.tokenContracts) {
    Object.entries(assetSibling.tokenContracts).forEach(([symbol, address]) => {
      if (!isPaymentCoinName(symbol)) {
        mergedContracts[trimString(symbol).toLowerCase()] = trimString(address);
      }
    });
  }

  const paymentSiblings = siblings.filter((sibling) => isPaymentCoinName(sibling?.coinName));
  const assetSiblings = siblings.filter((sibling) => !isPaymentCoinName(sibling?.coinName));

  const mergedAssetToken =
    (assetSibling ? resolveSettlementAssetToken(assetSibling) : null) ??
    assetSiblings.map((sibling) => resolveSettlementAssetToken(sibling)).find(Boolean) ??
    resolveSettlementAssetToken(wallet);

  const mergedPaymentToken =
    (paymentSibling ? resolveSettlementPaymentToken(paymentSibling) : null) ??
    paymentSiblings.map((sibling) => resolveSettlementPaymentToken(sibling)).find(Boolean) ??
    resolveSettlementPaymentToken(wallet);

  const paymentSettlement = paymentSibling
    ? getWalletSettlement(paymentSibling)
    : paymentSiblings.map((sibling) => getWalletSettlement(sibling)).find(Boolean);

  const mergedSettlement = siblings.reduce((acc, sibling) => {
    const settlement = getWalletSettlement(sibling);
    if (!settlement) return acc;
    return {
      ...acc,
      ...settlement,
      ...(settlement.spenderAddress || settlement.spender_address
        ? { spenderAddress: settlement.spenderAddress ?? settlement.spender_address }
        : {}),
      ...(settlement.settlementContract || settlement.settlement_contract
        ? {
            settlementContract:
              settlement.settlementContract ?? settlement.settlement_contract,
          }
        : {}),
      ...(settlement.eip712 ? { eip712: settlement.eip712 } : {}),
      ...(settlement.rpcUrl || settlement.rpc_url
        ? { rpcUrl: settlement.rpcUrl ?? settlement.rpc_url }
        : {}),
    };
  }, { ...(getWalletSettlement(wallet) ?? {}) });

  if (paymentSettlement) {
    const paymentTokenAddress = trimString(
      paymentSettlement.tokenAddress ?? paymentSettlement.token_address,
    );
    if (paymentTokenAddress) {
      mergedSettlement.tokenAddress = paymentTokenAddress;
    }
  }

  const assetSettlement = assetSibling
    ? getWalletSettlement(assetSibling)
    : assetSiblings.map((sibling) => getWalletSettlement(sibling)).find(Boolean);

  if (assetSettlement) {
    const assetToken = trimString(
      assetSettlement.assetToken ??
        assetSettlement.asset_token ??
        assetSettlement.assetTokenAddress ??
        assetSettlement.asset_token_address ??
        assetSettlement.tokenAddress ??
        assetSettlement.token_address,
    );
    if (assetToken) {
      mergedSettlement.assetToken = assetToken;
    }
  }

  const mergedRpcUrl =
    siblings
      .map((sibling) =>
        trimString(
          sibling?.rpcUrl ??
            getWalletSettlement(sibling)?.rpcUrl ??
            getWalletSettlement(sibling)?.rpc_url,
        ),
      )
      .find(Boolean) ?? trimString(wallet?.rpcUrl);

  return {
    ...wallet,
    settlement: Object.keys(mergedSettlement).length ? mergedSettlement : wallet.settlement,
    rpcUrl: mergedRpcUrl || wallet.rpcUrl || null,
    assetTokenAddress: mergedAssetToken ?? wallet.assetTokenAddress ?? null,
    paymentTokenAddress: mergedPaymentToken ?? wallet.paymentTokenAddress ?? null,
    tokenContracts: stripNativeTokenContracts({
      ...mergedContracts,
      ...wallet.tokenContracts,
    }),
  };
};

/** When toggling buy/sell, swap to the linked row that matches the payment asset. */
export const resolveWalletForPaymentSide = (
  wallets,
  selectedWallet,
  selectedPair,
  selectedSide,
) => {
  if (!selectedWallet) return null;

  const paymentAsset = getPaymentAssetForSide(selectedPair, selectedSide);
  if (!paymentAsset) return selectedWallet;

  if (walletMatchesSideForPair(selectedWallet, selectedPair, selectedSide, wallets)) {
    return selectedWallet;
  }

  const sibling = (wallets ?? []).find(
    (candidate) =>
      walletsShareIdentity(candidate, selectedWallet) &&
      walletMatchesSideForPair(candidate, selectedPair, selectedSide, wallets),
  );

  return sibling ?? null;
};

/** ERC-20 pair assets with no resolvable token contract (non-custodial only). */
export const getMissingErc20TokenContracts = (
  wallet,
  selectedPair,
  linkedWallets = [],
) => {
  if (!wallet || !selectedPair || isCustodialWallet(wallet)) return [];

  const missing = [];
  [selectedPair.baseCurrency, selectedPair.otherCurrency].forEach((assetSymbol) => {
    const key = trimString(assetSymbol).toLowerCase();
    if (!isErc20PairAsset(key)) return;

    const candidates = resolvePairLegTokenCandidates(
      wallet,
      linkedWallets,
      selectedPair,
      key,
    );
    if (!candidates.length) {
      missing.push(key);
    }
  });

  return missing;
};

export const formatMissingTokenContractMessage = (missingAssets, wallet) => {
  const labels = missingAssets.map((asset) => asset.toUpperCase()).join(', ');
  const settlement = getWalletSettlement(wallet);
  const settlementToken = trimString(
    settlement?.tokenAddress ?? settlement?.token_address,
  );

  if (settlement && !settlementToken) {
    return `${labels} balance cannot be loaded from your MetaMask wallet. The linked-wallets API returned settlement.tokenAddress as empty — backend must send the USDC ERC-20 contract address (where your tokens live), not the settlement contract.`;
  }

  return `${labels} balance cannot be loaded: token contract address is missing for this wallet.`;
};

export const formatMissingTokenContractBanner = (missingAssets) => {
  const labels = missingAssets.map((asset) => asset.toUpperCase()).join(', ');
  return `${labels} balance not shown — linked-wallets API must set settlement.tokenAddress to the USDC ERC-20 contract.`;
};
